import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SessionStorageService } from 'ngx-webstorage';
import {Observable, ReplaySubject, of, BehaviorSubject} from 'rxjs';
import {shareReplay, tap, catchError} from 'rxjs/operators';
import { HttpService } from '@cupoui/core/services/http.service';
import { StateStorageService } from '@cupoui/core/auth/state-storage.service';
import { Account } from '@cupoui/core/user/account.model';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class AccountService {
  private userIdentity: Account | null = null;
  private authenticationState = new ReplaySubject<Account | null>(1);
  private accountCache$?: Observable<Account | null>;

  private _languageChanged = new BehaviorSubject<string>('');
  public readonly languageChanged$ = this._languageChanged.asObservable();

  constructor(
    private translateService: TranslateService,
    private sessionStorage: SessionStorageService,
    private httpService: HttpService,
    private stateStorageService: StateStorageService,
    private router: Router
  ) {}

  save(account: Account): Observable<{}> {
    return this.httpService.post('/services/cupo-user-administration/api/account', account);
  }

  languageChanged(languageKey: string): void {
    this._languageChanged.next(languageKey);
  }

  requestOneTimePin(mail: string): Observable<{}> {
    return this.httpService.post('/services/cupo-user-administration/api/pin-send-by-mail', mail);
  }

  authenticate(identity: Account | null): void {
    this.userIdentity = identity;
    this.authenticationState.next(this.userIdentity);
  }

  hasAnyAuthority(authorities: string[] | string): boolean {
    if (!this.userIdentity || !this.userIdentity.authorities) {
      return false;
    }
    if (!Array.isArray(authorities)) {
      authorities = [authorities];
    }
    return this.userIdentity.authorities.some((authority: string) => authorities.includes(authority));
  }

  identity(force?: boolean): Observable<Account | null> {
    if (!this.accountCache$ || force || !this.isAuthenticated()) {
      this.accountCache$ = this.fetch().pipe(
        catchError((error) => {
          console.error('Error fetching account:', error);
          return of(null);
        }),
        tap((account: Account | null) => {
          this.authenticate(account);

          if (account && account.langKey) {
            const langKey = this.sessionStorage.retrieve('locale') || account.langKey;
            this.translateService.use(langKey);
          }

          if (account) {
            this.navigateToStoredUrl();
          }
        }),
        shareReplay()
      );
    }
    return this.accountCache$;
  }

  isAuthenticated(): boolean {
    return this.userIdentity !== null;
  }

  getAuthenticationState(): Observable<Account | null> {
    return this.authenticationState.asObservable();
  }

  getLogin(): string {
    return this.userIdentity ? this.userIdentity.login : '';
  }

  getFirstName(): string {
    return this.userIdentity ? this.userIdentity.firstName : '';
  }

  getFullName(): string {
    return this.userIdentity ? `${this.userIdentity.firstName} ${this.userIdentity.lastName}` : '';
  }

  getCustomerProfiles(): string[] {
    return this.userIdentity ? this.userIdentity.customerProfiles : [];
  }

  getCustomerRoles(): string[] {
    return this.userIdentity ? this.userIdentity.customerRoles : [];
  }

  private fetch(): Observable<Account> {
    return this.httpService.get<Account>('/services/cupo-user-administration/api/account').pipe(
      tap({
        error: (err) => console.error('API error details:', err)
      })
    );
  }

  private navigateToStoredUrl(): void {
    const previousUrl = this.stateStorageService.getUrl();
    if (previousUrl) {
      this.stateStorageService.clearUrl();
      this.router.navigateByUrl(previousUrl);
    } else if (this.router.url === '/auth/sign-in') {
      this.router.navigate(['']);
    }
  }
}
